//withour apply the get session_id api
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { ArrowLeft, Send, Sparkles } from 'lucide-react';
import { apiClient } from '@/core/api/client';
import { useRecaptchaGuestSession } from '@/features/guest-session/hooks/useRecaptchaGuestSession';
import { getGuestSessionToken } from '@/features/guest-session/utils/guestSessionStorage';
import { logChatResponse } from '@/core/services/chatSession.service';
import { clearFpQaHandoff, getFpQaHandoff } from '@/shared/utils/fpQaHandoff';
import { Button } from '@/shared/components/ui/button';
import { Textarea } from '@/shared/components/ui/textarea';
import { JourneyProgress } from './JourneyProgress';
import {
  ensureFpQaJourneySummary,
  summaryBulletsFromClassification,
} from './fpQaCompletion';
import { getChatUserType, sendChatMessage } from '../../../core/services/chatService';
import type { ChatRequestPayload, ChatResponsePayload } from '../../../core/services/chatService';

type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  quickReplies?: string[];
};

type CareStageData = Record<string, unknown> | null;

const CHAT_SESSION_STORAGE_KEY = 'chat_session_id';

const staticChatResponse: ChatResponsePayload = {
  session_id: 'id_007',
  response: '',
  status: 'in_progress',
  classification: null,
};

const careSeekerInitialMessages: Message[] = [
  {
    id: '1',
    role: 'assistant',
    content:
      "Hello! I'm your bQuest Navigator. I'm here to help you find the right care and build a personalized plan. To get started, tell me a little about what's going on: who needs care, and what's prompting your search right now.",
  },
];

const fpInitialMessages: Message[] = [
  {
    id: '1',
    role: 'assistant',
    content:
      "Hello! I'm your bQuest Navigator. I'll help you build a care plan for your client. To get started, tell me a little about what's going on: who needs care, and what's prompting your search right now.",
  },
];

export function ChatInterface() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { recaptchaElement, ensureGuestSession } = useRecaptchaGuestSession();
  // FP mode: if an FP-Q&A handoff is in flight, we use FP-skinned chrome
  // (2-step JourneyProgress: Your Story -> Your Matches) and FP-facing copy
  // around the chat input. The Q&A questions and AI flow are unchanged.
  const isFpMode = Boolean(getFpQaHandoff());
  const [careStageData, setCareStageData] = useState<CareStageData>(null);
  const [chatResponse, setChatResponse] = useState<ChatResponsePayload>(staticChatResponse);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [returnToSummary, setReturnToSummary] = useState<{ sessionId: string; careStage: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Auto-scroll-to-bottom should only follow the live conversation, never the
  // initial render / restored history — otherwise the page jumps past the
  // titles + stepper on load.
  const hasUserInteractedRef = useRef(false);
  const isClassificationConfirmed =
    chatResponse.status === 'confirmed' || chatResponse.status === 'classification_ready';

  useEffect(() => {
    const savedSessionId = sessionStorage.getItem('bquest_nav_session_id');
    const savedCareStage = sessionStorage.getItem('bquest_nav_care_stage');
    if (savedSessionId) {
      setReturnToSummary({ sessionId: savedSessionId, careStage: savedCareStage ?? '' });
    }
  }, []);

  useEffect(() => {
    if (!hasUserInteractedRef.current) return;
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);
  useEffect(() => {
    const initialMessages = isFpMode ? fpInitialMessages : careSeekerInitialMessages;
    const restoreConversation = async () => {
      try {
        // FORCE NEW CHAT
        const forceNewChat = sessionStorage.getItem('force_new_chat');

        if (forceNewChat === 'true') {
          sessionStorage.removeItem('force_new_chat');

          setSessionId(null);

          setMessages(initialMessages);

          return;
        }

        // GET SESSION
        const savedSessionId = localStorage.getItem(CHAT_SESSION_STORAGE_KEY);

        // NEW USER
        if (!savedSessionId) {
          setMessages(initialMessages);

          return;
        }

        // Guests can only restore once a guest token exists in this tab; without
        // it the request would 401 and bounce to /login. Start fresh instead.
        if (!isAuthenticated && !getGuestSessionToken()) {
          setMessages(initialMessages);
          return;
        }

        // RESTORE SESSION
        setSessionId(savedSessionId);

        // CALL API. Best-effort restore: a stale id (e.g. minted as a guest,
        // then logged in) returns 401 — opt out of the global /login bounce and
        // recover in the catch below.
        const response = await apiClient.get(`/chat-engine/conversation/${savedSessionId}`, {
          skipAuthRedirect: true,
        });

        const data = response.data;

        // RESTORE OLD CHAT
        if (data.messages?.length > 0) {
          const formattedMessages = data.messages.map(
            (message: { id: string; role: string; content: string }) => ({
              id: message.id,

              role: message.role.toLowerCase() as 'user' | 'assistant',

              content: message.content,
            })
          );

          setMessages(formattedMessages);
        } else {
          setMessages(initialMessages);
        }
      } catch (error) {
        // Drop a stale/unauthorized session id so the next entry starts clean
        // instead of repeatedly failing the restore.
        if ((error as { statusCode?: number })?.statusCode === 401) {
          localStorage.removeItem(CHAT_SESSION_STORAGE_KEY);
        }
        console.error('Failed to restore conversation:', error);

        setMessages(initialMessages);
      }
    };

    void restoreConversation();
  }, [isAuthenticated, isFpMode]);

  const handleSend = async (text?: string) => {
    const messageText = text || input.trim();
    if (!messageText || isTyping) {
      return;
    }
    // From here on the conversation is live — let the view follow new messages.
    hasUserInteractedRef.current = true;
    const updatedConversation = [
      ...messages,
      {
        id: `${Date.now()}`,
        role: 'user' as const,
        content: messageText,
      },
    ];

    const requestPayload: ChatRequestPayload = {
      session_id: sessionId,

      status: chatResponse.status || 'IN_PROGRESS',

      user_type: getChatUserType(user?.role),

      message: messageText,

      confirmed: true,

      care_stage_data: careStageData || null,

      conversation: updatedConversation.map((message) => ({
        role: message.role,
        content: message.content,
      })),
    };

    setMessages(updatedConversation);

    setInput('');
    setIsTyping(true);

    try {
      // Bot-gate guests before the first AI call: solve invisible reCAPTCHA and
      // mint a guest session token (no-op for logged-in users / once minted).
      await ensureGuestSession(isAuthenticated);
      const responseData = await sendChatMessage(requestPayload);
      setCareStageData(responseData.care_stage_data ?? null);
      const nextSessionId = responseData.session_id || sessionId;

      // Save the complete turn (user msg + AI response) to DB now that we have both
      if (nextSessionId) {
        const completeConversation = [
          ...requestPayload.conversation,
          ...(responseData.response
            ? [{ role: 'assistant' as const, content: responseData.response }]
            : []),
        ];
        void apiClient
          .post<{ received: true }>('/chat-engine/store-request', {
            ...requestPayload,
            session_id: nextSessionId,
            conversation: completeConversation,
          })
          .catch((err: unknown) => {
            console.error('[ChatEngine] Failed to store conversation:', err);
          });
      }

      setChatResponse(responseData);
      setSessionId(nextSessionId);
      if (
        nextSessionId &&
        responseData.status !== 'completed' &&
        responseData.status !== 'classification_ready'
      ) {
        localStorage.setItem(CHAT_SESSION_STORAGE_KEY, nextSessionId);
      }
      if (responseData.response) {
        const quickReplies = responseData.quick_reply_options
          ? responseData.quick_reply_options.split(',').map((item: string) => item.trim())
          : [];

        setMessages((previous) => [
          ...previous,
          {
            id: `${Date.now()}-assistant`,
            role: 'assistant',
            content: responseData.response,
            quickReplies,
          },
        ]);
      }

      if (responseData.status === 'completed' || responseData.status === 'classification_ready') {
        const targetSessionId = nextSessionId ?? '';
        const targetCareStage = responseData.classification?.care_stage ?? '';
        // FP Q&A skips care-seeker resume state; it creates an engagement below.
        const fpHandoff = getFpQaHandoff();

        if (!fpHandoff) {
          sessionStorage.setItem('bquest_nav_session_id', targetSessionId);
          sessionStorage.setItem('bquest_nav_care_stage', targetCareStage);
        }
        // Wait for session to be stored in DB before navigating so that
        // generate-journey-summary finds the complete session data
        await logChatResponse({
          user_id: user?.id ?? null,
          session_id: responseData.session_id,
          response: responseData.response,
          status: responseData.status,
          classification: responseData.classification,
          care_stage_data: responseData.care_stage_data,
        });

        if (fpHandoff) {
          if (responseData.classification?.is_possible === false) {
            // No matches to snapshot — route to the shared concierge funnel.
            clearFpQaHandoff();
            navigate('/detailed-care-navigator/concierge');
            return;
          }
          if (!targetCareStage) {
            setMessages((previous) => [
              ...previous,
              {
                id: `${Date.now()}-error`,
                role: 'assistant',
                content:
                  "Something went wrong while saving this Q&A engagement (missing classified care stage). Please try again.",
              },
            ]);
            return;
          }
          const summaryBullets = summaryBulletsFromClassification(responseData);
          try {
            await ensureFpQaJourneySummary(targetSessionId).catch((err) => {
              console.warn('[FP Q&A] Failed to ensure journey summary:', err);
            });
            const { data: engagement } = await apiClient.post<{ id: string }>(
              '/financial-advisors/me/qa-engagements',
              {
                navigatorSessionId: targetSessionId,
                careStageName: targetCareStage,
                ...(summaryBullets.length ? { summaryBullets } : {}),
                ...(fpHandoff.clientUserId ? { clientUserId: fpHandoff.clientUserId } : {}),
                ...(fpHandoff.name ? { name: fpHandoff.name } : {}),
              },
            );
            clearFpQaHandoff();
            navigate(`/dashboard/financial-advisor?fp_qa_engagement=${engagement.id}`);
          } catch (err) {
            console.error('[FP Q&A] Failed to create engagement:', err);
            setMessages((previous) => [
              ...previous,
              {
                id: `${Date.now()}-error`,
                role: 'assistant',
                content:
                  'Something went wrong while saving your Q&A engagement. Please try again.',
              },
            ]);
          }
          return;
        }
        if (responseData.classification?.is_possible === false) {
          navigate('/detailed-care-navigator/concierge');
        } else {
          navigate('/detailed-care-navigator/situation-summary', {
            state: { sessionId: targetSessionId, careStage: targetCareStage },
          });
        }
      }
    } catch (error) {
      console.error('Failed to send chat message:', error);
      setMessages((previous) => [
        ...previous,
        {
          id: `${Date.now()}-error`,
          role: 'assistant',
          content: 'Something went wrong while contacting the care assistant. Please try again.',
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas">
      {recaptchaElement}
      <JourneyProgress
        currentStep={isClassificationConfirmed ? (isFpMode ? 'matches' : 'summary') : 'intake'}
        mode={isFpMode ? 'fp' : 'care-seeker'}
      />

      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto w-full max-w-3xl">
          {isFpMode && (
            <button
              type="button"
              onClick={() => navigate('/dashboard/financial-advisor')}
              className="mb-6 inline-flex items-center gap-1 text-sm text-text-secondary transition-colors duration-150 ease-out hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back to Care Plans
            </button>
          )}

          <div className="mb-2 flex items-center justify-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sage text-white">
              <Sparkles className="h-4 w-4" aria-hidden />
            </span>
            <h1 className="font-termina font-medium text-2xl text-text-primary">bQuest Navigator</h1>
          </div>
          <p className="mb-6 text-center text-sm text-text-secondary">
            {isFpMode
              ? 'AI-powered care planning for your client'
              : 'AI-powered care planning assistant'}
          </p>

          {returnToSummary && !isFpMode && (
            <div className="mb-4 flex justify-end">
              <Button
                size="sm"
                onClick={() =>
                  navigate('/detailed-care-navigator/situation-summary', {
                    state: {
                      sessionId: returnToSummary.sessionId,
                      careStage: returnToSummary.careStage,
                    },
                  })
                }
                className="h-9 bg-sage text-white transition-colors duration-150 ease-out hover:bg-[hsl(169_95%_18%)]"
              >
                View My Assessment
              </Button>
            </div>
          )}

          <div className="rounded-2xl border border-border-subtle bg-card-base shadow-[0_1px_2px_rgba(20,30,28,0.04),0_4px_14px_rgba(20,30,28,0.04)]">
            <div className="max-h-[560px] min-h-[420px] space-y-5 overflow-y-auto p-6">
              {messages.map((message) => (
                <div key={message.id}>
                  <div
                    className={`flex gap-3 ${
                      message.role === 'user' ? 'justify-end' : 'items-start'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-sage text-white">
                        <Sparkles className="h-4 w-4" aria-hidden />
                      </span>
                    )}
                    <div
                      className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        message.role === 'user'
                          ? 'bg-sage text-white'
                          : 'bg-card-subtle text-text-primary'
                      }`}
                    >
                      <p>{message.content}</p>
                    </div>
                  </div>

                  {message.role === 'assistant' &&
                    message.quickReplies &&
                    message.quickReplies.length > 0 && (
                      <div className="ml-11 mt-3 flex flex-wrap gap-2">
                        {message.quickReplies.map((reply) => (
                          <button
                            key={reply}
                            type="button"
                            onClick={() => handleSend(reply)}
                            className="rounded-full border border-border-subtle bg-card-base px-4 py-1.5 text-sm font-medium text-text-primary transition-colors duration-150 ease-out hover:border-sage hover:bg-sage-bg hover:text-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
                          >
                            {reply}
                          </button>
                        ))}
                      </div>
                    )}
                </div>
              ))}

              {isTyping && (
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-sage text-white">
                    <Sparkles className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="flex items-center gap-1 rounded-2xl bg-card-subtle px-4 py-3">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-tertiary [animation-delay:-0.32s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-tertiary [animation-delay:-0.16s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-tertiary" />
                  </div>
                </div>
              )}

              <div ref={scrollRef} />
            </div>

            <div className="border-t border-border-subtle p-3">
              <div className="flex items-center gap-2 rounded-3xl bg-card-subtle p-2">
                <Textarea
                  ref={textareaRef}
                  placeholder="Type your response…"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void handleSend();
                    }
                  }}
                  className="min-h-[40px] max-h-[120px] flex-1 resize-none border-0 bg-transparent p-2 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  rows={1}
                />
                <Button
                  onClick={() => void handleSend()}
                  disabled={!input.trim() || isTyping}
                  className="h-9 w-9 flex-shrink-0 rounded-full bg-sage p-0 transition-colors duration-150 ease-out hover:bg-[hsl(169_95%_18%)] disabled:bg-sage/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
                  aria-label="Send"
                >
                  <Send className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
